/**
 * PermissionFormDialog — create or edit a permission.
 *
 * Create mode: all fields editable (code is required).
 * Edit mode:   code field is disabled (read-only after creation per spec).
 */
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '@ui-kit';
import { permissionCreateSchema, permissionUpdateSchema } from '../schema/permissionSchema';
import type { PermissionCreateFormValues } from '../schema/permissionSchema';
import type { PermissionItem } from '../types/role';

interface PermissionFormDialogProps {
  open: boolean;
  /** When provided, the dialog is in edit mode. */
  editItem?: PermissionItem;
  onClose: () => void;
  onSubmit: (values: PermissionCreateFormValues) => void;
  isLoading?: boolean;
  errorMessage?: string;
}

export function PermissionFormDialog({
  open,
  editItem,
  onClose,
  onSubmit,
  isLoading = false,
  errorMessage,
}: PermissionFormDialogProps) {
  const { t } = useTranslation();
  const isEditMode = !!editItem;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PermissionCreateFormValues>({
    resolver: zodResolver(isEditMode ? permissionUpdateSchema : permissionCreateSchema),
    defaultValues: {
      code: '',
      resource: '',
      action: '',
      nameKo: '',
      description: '',
    },
  });

  // Populate form when edit item changes
  useEffect(() => {
    if (open && editItem) {
      reset({
        code: editItem.code,
        resource: editItem.resource,
        action: editItem.action,
        nameKo: editItem.nameKo,
        description: editItem.description ?? '',
      });
    } else if (!open) {
      reset({ code: '', resource: '', action: '', nameKo: '', description: '' });
    }
  }, [open, editItem, reset]);

  // Esc 키 닫기
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const handleFormSubmit = handleSubmit((values) => {
    onSubmit(values);
  });

  const fieldStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-1)',
    marginBottom: 'var(--spacing-3)',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
  };

  const inputStyle: React.CSSProperties = {
    padding: 'var(--spacing-2) var(--spacing-3)',
    background: 'var(--color-surface-elevated)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-sans)',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  };

  const disabledInputStyle: React.CSSProperties = {
    ...inputStyle,
    background: 'var(--color-surface-muted)',
    color: 'var(--color-text-muted)',
    cursor: 'not-allowed',
    fontFamily: 'var(--font-family-mono)',
  };

  const errorStyle: React.CSSProperties = {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-danger)',
    marginTop: 'var(--spacing-1)',
  };

  const hintStyle: React.CSSProperties = {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    marginTop: 'var(--spacing-1)',
  };

  return (
    <FormDialog
      open={open}
      title={
        isEditMode
          ? t('permission.form.title.edit')
          : t('permission.form.title.create')
      }
      onClose={onClose}
      onSubmit={() => void handleFormSubmit()}
      submitLabel={
        isLoading
          ? t('permission.form.button.loading')
          : isEditMode
          ? t('permission.form.button.update')
          : t('permission.form.button.submit')
      }
      cancelLabel={t('permission.form.button.cancel')}
    >
      <div>
        {/* 에러 배너 */}
        {errorMessage && (
          <div
            role="alert"
            style={{
              marginBottom: 'var(--spacing-3)',
              padding: 'var(--spacing-3)',
              background:
                'color-mix(in srgb, var(--color-danger) 12%, var(--color-surface-muted))',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-danger)',
            }}
          >
            {errorMessage}
          </div>
        )}

        {/* 권한 코드 */}
        <div style={fieldStyle}>
          <label htmlFor="perm-form-code" style={labelStyle}>
            {t('permission.form.field.code')} {!isEditMode && '*'}
          </label>
          {isEditMode ? (
            <>
              <input
                id="perm-form-code"
                type="text"
                value={editItem?.code ?? ''}
                disabled
                aria-disabled="true"
                aria-describedby="perm-form-code-readonly"
                style={disabledInputStyle}
              />
              <span id="perm-form-code-readonly" style={hintStyle}>
                {t('permission.form.field.code.readonly')}
              </span>
            </>
          ) : (
            <>
              <input
                id="perm-form-code"
                type="text"
                placeholder={t('permission.form.field.code.placeholder')}
                {...register('code')}
                aria-invalid={!!errors.code}
                aria-describedby={errors.code ? 'perm-code-error' : 'perm-code-hint'}
                style={{
                  ...inputStyle,
                  fontFamily: 'var(--font-family-mono)',
                  borderColor: errors.code
                    ? 'var(--color-danger)'
                    : 'var(--color-surface-border)',
                }}
              />
              {errors.code ? (
                <span id="perm-code-error" style={errorStyle} role="alert">
                  {errors.code.message}
                </span>
              ) : (
                <span id="perm-code-hint" style={hintStyle}>
                  {t('permission.form.field.code.hint')}
                </span>
              )}
            </>
          )}
        </div>

        {/* 리소스 */}
        <div style={fieldStyle}>
          <label htmlFor="perm-form-resource" style={labelStyle}>
            {t('permission.form.field.resource')} *
          </label>
          <input
            id="perm-form-resource"
            type="text"
            placeholder={t('permission.form.field.resource.placeholder')}
            {...register('resource')}
            aria-invalid={!!errors.resource}
            aria-describedby={errors.resource ? 'perm-resource-error' : 'perm-resource-hint'}
            style={{
              ...inputStyle,
              borderColor: errors.resource
                ? 'var(--color-danger)'
                : 'var(--color-surface-border)',
            }}
          />
          {errors.resource ? (
            <span id="perm-resource-error" style={errorStyle} role="alert">
              {errors.resource.message}
            </span>
          ) : (
            <span id="perm-resource-hint" style={hintStyle}>
              {t('permission.form.field.resource.hint')}
            </span>
          )}
        </div>

        {/* 액션 */}
        <div style={fieldStyle}>
          <label htmlFor="perm-form-action" style={labelStyle}>
            {t('permission.form.field.action')} *
          </label>
          <input
            id="perm-form-action"
            type="text"
            placeholder={t('permission.form.field.action.placeholder')}
            {...register('action')}
            aria-invalid={!!errors.action}
            aria-describedby={errors.action ? 'perm-action-error' : 'perm-action-hint'}
            style={{
              ...inputStyle,
              borderColor: errors.action
                ? 'var(--color-danger)'
                : 'var(--color-surface-border)',
            }}
          />
          {errors.action ? (
            <span id="perm-action-error" style={errorStyle} role="alert">
              {errors.action.message}
            </span>
          ) : (
            <span id="perm-action-hint" style={hintStyle}>
              {t('permission.form.field.action.hint')}
            </span>
          )}
        </div>

        {/* 한글 이름 */}
        <div style={fieldStyle}>
          <label htmlFor="perm-form-nameKo" style={labelStyle}>
            {t('permission.form.field.nameKo')} *
          </label>
          <input
            id="perm-form-nameKo"
            type="text"
            placeholder={t('permission.form.field.nameKo.placeholder')}
            {...register('nameKo')}
            aria-invalid={!!errors.nameKo}
            aria-describedby={errors.nameKo ? 'perm-nameKo-error' : undefined}
            style={{
              ...inputStyle,
              borderColor: errors.nameKo
                ? 'var(--color-danger)'
                : 'var(--color-surface-border)',
            }}
          />
          {errors.nameKo && (
            <span id="perm-nameKo-error" style={errorStyle} role="alert">
              {errors.nameKo.message}
            </span>
          )}
        </div>

        {/* 설명 */}
        <div style={fieldStyle}>
          <label htmlFor="perm-form-description" style={labelStyle}>
            {t('permission.form.field.description')}
          </label>
          <textarea
            id="perm-form-description"
            placeholder={t('permission.form.field.description.placeholder')}
            rows={2}
            {...register('description')}
            style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
          />
        </div>
      </div>
    </FormDialog>
  );
}
